import { useQueryClient } from '@tanstack/react-query';
import {
  BrainCircuit,
  Database,
  Activity,
  ClipboardCheck,
  Gauge,
  Globe,
  LayoutDashboard,
  LogOut,
  type LucideIcon,
  Network,
  Package,
  PackagePlus,
  Scale,
  TableProperties,
  Send,
  PackageSearch,
  Shirt,
  Workflow,
  Tags,
  Images,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { signOut } from '../../features/auth/auth.api';
import { mainNav, type NavIcon, type NavItem } from '../../routes/nav-links';
import { pageRoutes } from '../../routes/page-routes';
import { useSession } from '../../shared/api/session';
import { cn } from '../../shared/ui/cn';

const ICONS: Record<NavIcon, LucideIcon> = {
  dashboard: LayoutDashboard,
  imports: PackagePlus,
  products: Package,
  review: ClipboardCheck,
  publish: Send,
  fetch: Globe,
  proxies: Network,
  jobs: Activity,
  tester: Scale,
  weightTable: TableProperties,
  weightModel: BrainCircuit,
  ubuyData: Database,
  storeRules: Shirt,
  storeTester: PackageSearch,
  imageComparison: Images,
  howWeights: Workflow,
  ai: Gauge,
  categories: Tags,
};

const ITEM =
  'relative flex items-center gap-3 rounded-control px-3 py-2 text-sm font-medium text-sidebar-muted no-underline';

/** The menu: the import flow step by step (the steps still being built are shown, greyed), then the rest. */
export function Sidebar({ onNavigate }: { onNavigate: () => void }) {
  return (
    <aside className="flex h-full flex-col bg-sidebar text-sidebar-ink">
      <div className="flex items-center gap-3 px-5 py-5">
        <span
          aria-hidden="true"
          className="grid size-9 place-items-center rounded-control bg-accent text-sm font-bold text-white"
        >
          SK
        </span>
        <span className="grid leading-tight">
          <span className="font-bold">Sawa Kart</span>
          <span className="text-xs text-sidebar-muted">Import system</span>
        </span>
      </div>

      <nav aria-label="Main" className="flex-1 overflow-y-auto px-3 pb-4">
        {mainNav.map((section) => (
          <div key={section.title} className="mt-4 first:mt-0">
            <p className="px-3 pb-1.5 text-xs font-semibold tracking-wider text-sidebar-muted uppercase">
              {section.title}
            </p>
            <ul className="grid gap-0.5">
              {section.items.map((item) => (
                <li key={item.label}>
                  <Item item={item} onNavigate={onNavigate} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <UserBox />
    </aside>
  );
}

function Item({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  const Icon = ICONS[item.icon];
  if (item.kind === 'soon') {
    return (
      <span className={cn(ITEM, 'cursor-default opacity-60')} aria-disabled="true">
        <Icon aria-hidden="true" className="size-4.5 shrink-0" />
        <span className="flex-1">{item.label}</span>
        <span className="rounded-full border border-sidebar-line px-1.5 text-xs">{item.when}</span>
      </span>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          ITEM,
          'hover:bg-sidebar-hover hover:text-sidebar-ink',
          isActive &&
            'bg-sidebar-hover text-sidebar-ink before:absolute before:inset-y-1.5 before:left-0 before:w-1 before:rounded-full before:bg-accent',
        )
      }
    >
      <Icon aria-hidden="true" className="size-4.5 shrink-0" />
      <span className="flex-1">{item.label}</span>
      {item.step ? <Step n={item.step} /> : null}
    </NavLink>
  );
}

/** The step number of the import flow ("1", "2"…). */
function Step({ n }: { n: number }) {
  return (
    <>
      <span className="sr-only">, step {n}</span>
      <span
        aria-hidden="true"
        className="grid size-5 place-items-center rounded-full border border-sidebar-line text-xs tabular-nums"
      >
        {n}
      </span>
    </>
  );
}

function UserBox() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);

  const leave = async () => {
    setLeaving(true);
    try {
      await signOut();
    } finally {
      queryClient.clear();
      await navigate(pageRoutes.login, { replace: true });
    }
  };

  const email = user?.email ?? '';
  return (
    <div className="flex items-center gap-3 border-t border-sidebar-line px-4 py-4">
      <span
        aria-hidden="true"
        className="grid size-8 shrink-0 place-items-center rounded-full bg-sidebar-hover text-sm font-semibold uppercase"
      >
        {email.charAt(0) || '?'}
      </span>
      <span className="min-w-0 flex-1 truncate text-label text-sidebar-muted" title={email}>
        {email}
      </span>
      <button
        type="button"
        onClick={() => void leave()}
        disabled={leaving}
        aria-busy={leaving}
        aria-label="Sign out"
        title="Sign out"
        className="grid size-8 cursor-pointer place-items-center rounded-control text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-ink disabled:opacity-55"
      >
        <LogOut aria-hidden="true" className="size-4" />
      </button>
    </div>
  );
}
